/* eslint-disable camelcase */
import React, { useState } from 'react';
import {
  makeStyles, Typography, Box, Grid, Container, Divider,
} from '@material-ui/core';
import { withTranslation } from 'config/i18n';
import PropTypes from 'prop-types';
import Breadcrumbs from 'common/components/breadcrumbs/Breadcrumbs';
import ERPagination from 'components/ERPagination';
import axios, { API } from 'config/axios';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { serverFetch } from 'utils/serverFetch';
import generateHreflang from 'utils/generateHreflang';
import generateMetaData from 'utils/generateMetaData';
import Link from 'components/shared/Link';
import { useLayoutLoadingSpinner } from 'components/shared/Layout';

const useStyles = makeStyles((theme) => ({
  destinationContainer: {
    backgroundColor: theme.palette.common.white,
    paddingBottom: 46,
  },
  pageTitle: {
    fontSize: theme.typography.pxToRem(28),
    color: theme.palette.grey[900],
    padding: `${theme.typography.pxToRem(64)} 0`,
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
    color: theme.palette.grey[700],
    marginBottom: theme.typography.pxToRem(10),
  },
  destinationsRow: {
    display: 'flex',
  },
  destinationLink: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    display: 'block',
    marginBottom: theme.spacing(1),
  },
}));

const Siteindex = ({ t, pageData, headLinks }) => {
  const classes = useStyles();
  const router = useRouter();
  const [data, setData] = useState(pageData);
  const [, setLoading] = useLayoutLoadingSpinner();

  const createBreadCrumbProps = () => {
    const breadCrumbLinks = [];

    breadCrumbLinks.push({
      text: t('fe_siteindex:sitemap_index'),
      path: data.routes.site_index_path,
    });
    data.current_place
      && (data.country
        && breadCrumbLinks.push({
          text: data.country.name,
          path: `${data.routes.site_index_path}/${data.country.slug}`,
        }),
      data.region
        && breadCrumbLinks.push({
          text: data.region.name,
          path: `${data.routes.site_index_path}/${data.region.slug}`,
        }),
      !(data.current_place.type == 'region' || data.current_place.type == 'country')
        && breadCrumbLinks.push({
          text: data.current_place.name,
          path: `${data.routes.site_index_path}/${data.current_place.slug}`,
        }));

    const compactLinks = breadCrumbLinks.filter((link) => link != null);
    const pageTitle = compactLinks.slice(-1)[0];

    const breadCrumbsProps = {
      links: compactLinks.slice(0, -1),
      breadCrumbsTitle: pageTitle ? pageTitle.text : '',
    };

    return breadCrumbsProps;
  };

  function chunkArray(myArray, chunk_size) {
    let index = 0;
    const arrayLength = myArray.length;
    const tempArray = [];
    for (index = 0; index < arrayLength; index += chunk_size) {
      const myChunk = myArray.slice(index, index + chunk_size);
      tempArray.push(myChunk);
    }
    return tempArray;
  }

  const generateMakeTypesLinks = (arr) => {
    const links = arr.map((obj) => {
      let path;
      if (data.current_place) {
        path = data.routes.vehicle_make_or_type_and_place_path
          .replace('$vehicle_make_or_type', obj.slug)
          .replace('$place', data.current_place.slug);
      } else {
        path = data.routes.vehicle_make_or_type_path.replace('$vehicle_make_or_type', obj.slug);
      }

      obj.path = path;
      return obj;
    });

    const chunkedArr = chunkArray(links, Math.ceil(arr.length / 3));
    return chunkedArr;
  };

  const generateDestinationsLinks = () => {
    const links = data.places.map((obj) => {
      let path;
      path = data.routes.place_site_index_path.replace('$place', obj.slug);

      obj.path = path;
      return obj;
    });
    const chunkedArr = chunkArray(links, Math.ceil(data.places.length / 3));
    return chunkedArr;
  };

  const sectionCreator = (header, links, type_make) => (
    <Box mb={6}>
      <Typography className={classes.sectionTitle}>{header.text}</Typography>
      <Grid container spacing={1} className={classes.destinationsRow}>
        {links.map((chunk, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Grid item xs={12} md={6} lg={4} key={index}>
            {chunk.map((link) => (
              <Link
                href={link.path}
                className={classes.destinationLink}
                key={`${link.slug}`}
                component="a"
              >
                {type_make
                  ? data.current_place
                    ? `${link.name} ${t('fe_siteindex:rentals')} ${t('siteindex:in')} ${
                      data.current_place.name
                    }`
                    : `${link.name} ${t('fe_siteindex:rentals')}`
                  : link.name}
              </Link>
            ))}
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const handlePageChange = (page) => {
    setLoading(true);
    let apiLink;
    if (Object.keys(router.query).length === 0) {
      apiLink = API.siteIndex.siteIndex;
    } else if (router.query.place) {
      apiLink = API.siteIndex.filteredSiteIndex.replace('{place}', router.query.place);
    }
    (async () => {
      try {
        const result = await axios.get(apiLink, {
          params: {
            page,
          },
        });
        setData(result.data);
      } catch (err) {
        switch (err.response.status) {
          case 400:
            if (err.response.data) {
              setLoading(true);
              let redirectLink;
              if (router.query.subpath) {
                redirectLink = `${router.query.subpath}${API.siteIndex.filteredSiteIndex.replace(
                  '{place}',
                  err.response.data.new_slug,
                )}`;
              } else {
                redirectLink = `${API.siteIndex.filteredSiteIndex.replace(
                  '{place}',
                  err.response.data.new_slug,
                )}`;
              }
              router.push({
                pathname: redirectLink,
                params: { page },
              });
            }
            break;
          case 404:
            pageData = err.response.status;

            break;
          default:
            break;
        }
      }
    })();
  };

  React.useEffect(() => {
    setLoading(false);
  }, [data]);

  // useEffect(() => {
  //   // ReDraw content when route.query changes from the browser back button
  //   handlePageChange(router.query.page);
  // }, [router.query]);

  return (
    <>
      <Head>
        {data.meta_data && generateMetaData(data.meta_data)}
        {!process.browser && (
          <>
            <link rel="canonical" href={headLinks.canonical} />
            {headLinks.urls.map((local) => (
              <link
                key={local.hreflang}
                rel="alternate"
                href={local.url}
                hrefLang={local.hreflang}
              />
            ))}
          </>
        )}
      </Head>
      <Box className={classes.destinationContainer}>
        <Container>
          <Breadcrumbs data={createBreadCrumbProps()} />
          <Divider light />
          <Typography className={classes.pageTitle}>
            {`${data.current_place ? data.current_place.name : ''} ${t('fe_siteindex:sitemap')}`}
          </Typography>
          {/* all rentals */}
          {/* <Box mb={6}>
            <Typography className={classes.sectionTitle}>
              {`${t("fe_siteindex:all_rentals")}
                ${
                  data.current_place
                    ? `${t("fe_siteindex:in")} ${data.current_place.name}`
                    : ""
                }
              `}
            </Typography>
            <Grid container spacing={1} className={classes.destinationsRow}>
              <Grid item xs={12} md={6} lg={4}>
                <Link
                  href={
                    data.current_place
                      ? data.routes.place_rentals_path.replace(
                          "$place",
                          data.current_place.slug
                        )
                      : data.routes.all_rentals_path
                  }
                  className={classes.destinationLink}
                >
                  {`${t("fe_siteindex:all_rentals")} ${
                    data.current_place
                      ? `${t("fe_siteindex:in")} ${data.current_place.name}`
                      : ""
                  }`}
                </Link>
              </Grid>
            </Grid>
          </Box> */}
          {/* types */}
          {data.vehicle_types.length
            ? sectionCreator(
              {
                text: data.current_place
                  ? `${t('fe_siteindex:all_rentals')} ${t('siteindex:types')} ${t(
                    'fe_siteindex:in',
                  )} ${data.current_place.name}`
                  : `${t('fe_siteindex:all_rentals')} ${t('fe_siteindex:types')}`,
              },
              generateMakeTypesLinks(data.vehicle_types),
              'type_make',
            )
            : null}
          {/* makes */}
          {data.vehicle_makes.length
            ? sectionCreator(
              {
                text: data.current_place
                  ? `${t('fe_siteindex:all_rentals')} ${t('siteindex:makes')} ${t(
                    'fe_siteindex:in',
                  )} ${data.current_place.name}`
                  : `${t('fe_siteindex:all_rentals')} ${t('fe_siteindex:makes')}`,
              },
              generateMakeTypesLinks(data.vehicle_makes),
              'type_make',
            )
            : null}
          {/* Destinations */}
          {data.places.length
            ? sectionCreator(
              {
                text: t('fe_siteindex:destinations'),
              },
              generateDestinationsLinks(),
            )
            : null}
          {data.current_place && data.pagination.pages_count > 1 && (
            <ERPagination
              data={data.pagination}
              url={`${router.pathname}/${router.query.place}`}
              handleChange={(page) => handlePageChange(page)}
              link
            />
          )}
        </Container>
      </Box>
    </>
  );
};

Siteindex.getInitialProps = async (ctx) => {
  // generate canonical and hrefLang
  const host = ctx.req ? ctx.req.headers.host : window.location.host;
  let url;
  ctx.req ? (url = ctx.req.url) : (url = ctx.asPath);
  let headLinks = {};
  let apiLink = '';
  // if (!process.browser) {
  headLinks = generateHreflang(url, '/siteindex', host);
  // }

  if (ctx.query.place) {
    apiLink = API.siteIndex.filteredSiteIndex.replace('{place}', ctx.query.place);
  } else {
    apiLink = API.siteIndex.siteIndex;
  }

  let pageData = {};

  // if (!process.browser) {
  const { success, data, error } = await serverFetch(ctx, apiLink, {
    page: ctx.query.page || 1,
  });
  if (success && data) pageData = data;
  else {
    pageData = error.response.status;
    switch (error.response.status) {
      case 400:
        if (error.response.data) {
          const redirectLink = ctx.query.page
            ? `${API.siteIndex.filteredSiteIndex
              .replace('{place}', error.response.data.new_slug)
              .replace('{page}', ctx.query.page)}`
            : `${API.siteIndex.filteredSiteIndex.replace('{place}', error.response.data.new_slug)}`;
          ctx.res.writeHead(302, {
            Location: redirectLink,
          });
          ctx.res.end();
        }
        break;
      case 404:
        pageData = error.response.status;
        ctx.res.statusCode = 503;
        ctx.res.end();
        break;
      default:
        break;
    }
  }
  // }

  return {
    namespacesRequired: ['fe_siteindex', 'pagination'],
    pageData,
    headLinks,
    er: true,
  };
};

Siteindex.propTypes = {
  t: PropTypes.func.isRequired,
};
export default withTranslation('fe_siteindex')(Siteindex);
